// ── logs-helpers: the distinct-log VIEWS over the one real /event-history read ─
// Wave 10 (Logs/History). CONTRACT TRUTH (apps/vjal-coaching-api/src): there is
// exactly ONE event read — GET /event-history (staff-only, mode 'all') — and NO
// dedicated invite/report-gen/participant/session log routes. The PRD names
// several "distinct logs"; we present each as a FILTERED VIEW over the single
// real read, keyed on the taxonomy `category` the backend already stamps on each
// event (foundation/participant/session/integration[invite,zoom]/report/
// transcript/resource/permission/user/profile/platform_event/migration). We
// NEVER fabricate a second log source: a view with no matching events shows the
// honest empty body, and the raw audit/event-history view is the unfiltered
// table. Which views are real (route-backed) vs. honest is explicit below.

import { humanizeLabel } from "@vjal/ui";

import type { CoachingHistoryEventDto } from "../../services/coaching-dtos";

// ── The log VIEWS (all are filtered projections of /event-history) ───────────
// `categories` is the taxonomy-category allow-list for the view; an empty
// `categories` means the ALL-events audit view (no filter). Every view is REAL
// (backed by the one event-history route) — there is no fabricated log here.
export interface LogView {
  id: string;
  label: string;
  description: string;
  /** Taxonomy categories included in this view (empty = all events). */
  categories: string[];
}

export const LOG_VIEWS: LogView[] = [
  {
    id: "audit",
    label: "Audit log (all events)",
    description: "Every append-only coaching event, newest first. Staff-only; sensitive values redacted.",
    categories: []
  },
  {
    id: "invites",
    label: "Invite log",
    description: "Invite send/resend requests and their integration evidence (a view over event-history).",
    categories: ["integration"]
  },
  {
    id: "reports",
    label: "Report-generation log",
    description: "Report review/approve/download-readiness events (a view over event-history).",
    categories: ["report", "transcript"]
  },
  {
    id: "participants",
    label: "Participant history",
    description: "Participant create/attach/membership/removal events (a view over event-history).",
    categories: ["participant"]
  },
  {
    id: "sessions",
    label: "Session history",
    description: "Session create/series/reschedule/lifecycle events (a view over event-history).",
    categories: ["session"]
  },
  {
    id: "access",
    label: "Access / denied-action log",
    description: "Permission-denied (blocked mutation) events — the audit trail for refused changes.",
    categories: ["permission"]
  }
];

const VIEW_BY_ID = new Map<string, LogView>(LOG_VIEWS.map((view) => [view.id, view]));

export function getLogView(id: string | null | undefined): LogView {
  return (id && VIEW_BY_ID.get(id)) || LOG_VIEWS[0];
}

/**
 * Filter the events to a log view (by taxonomy category). The empty-category
 * "audit" view returns every event. Events whose category is unknown fall into
 * the audit view only — never silently into a named view (no misattribution).
 */
export function filterEventsForView(events: CoachingHistoryEventDto[], view: LogView): CoachingHistoryEventDto[] {
  if (view.categories.length === 0) return events;
  const allowed = new Set(view.categories);
  return events.filter((event) => (event.category ? allowed.has(event.category) : false));
}

// ── Display helpers (humanizeLabel for every enum; never a raw value) ────────
export function eventActionLabel(action: string | null | undefined): string {
  return action ? humanizeLabel(action) : "—";
}

export function eventCategoryLabel(category: string | null | undefined): string {
  return category ? humanizeLabel(category) : "—";
}

export function eventActorLabel(event: CoachingHistoryEventDto): string {
  const actor = (event.actor ?? "").trim();
  if (actor) return actor;
  const id = (event.actor_id ?? "").trim();
  return id ? `Actor ${id}` : "System";
}

/** A safe one-line summary for an event row — never a raw payload. */
export function eventSummary(event: CoachingHistoryEventDto): string {
  const summary = (event.summary ?? "").trim();
  if (summary) return summary;
  const fields = event.changed_fields;
  if (Array.isArray(fields) && fields.length > 0) return `Changed: ${fields.join(", ")}`;
  return eventActionLabel(event.action);
}

/** Whether an event is a denied/blocked action (warning tone in the table). */
export function isDeniedActionEvent(event: CoachingHistoryEventDto): boolean {
  return event.category === "permission" || /denied|blocked/i.test(event.action ?? "");
}
